<template>
  <div class="dropdown">
    <button
      v-if="hasOnlyOneAction"
      @click="tryPerformAction(orderedActions[0])"
      :disabled="isPerformingAction || isInDetailViewEditMode"
      type="button"
      :class="singleActionButtonClass"
    >
      <template v-if="generatingDocument">
        <xpinner :has-background="false" color="White" speed="fast"/>
      </template>
      {{orderedActions[0].actionName}}
    </button>
    <template v-else-if="orderedActions.length > 0">
      <button
        :class="multiActionButtonClass"
        :disabled="isPerformingAction || isInDetailViewEditMode"
        :id="`actionsDropdown-${dropdownControlId}`"
        data-toggle="dropdown"
        data-boundary="viewport"
        aria-haspopup="true"
        aria-expanded="false"
      >
        <template v-if="generatingDocument">
          <xpinner :has-background="false" color="White" speed="fast"/>
        </template>
        Actions
      </button>

      <ul class="dropdown-menu dropdown-menu-right uncover w-fit-i max-w-xs" :aria-labelledby="`actionsDropdown-${dropdownControlId}`">
        <li v-for="action in orderedActions" :key="action.action_client_id">
          <button
            type="button"
            class="dropdown-item"
            @click="tryPerformAction(action)"
          >{{action.actionName}}</button>
        </li>
      </ul>
    </template>

    <action-confirmation-modal
      v-if="modalStateFor(vueModalID.confirmObjectModelAction).isVisible"
      :state="modalStateFor(vueModalID.confirmObjectModelAction)"
      :index="vueModalID.confirmObjectModelAction"
      @toggle-modal="handleCloseConfirmationModal"
      :overrideConfig="actionConfirmationModalConfig"
      :context="NotesTabContext.Records"
      :entry="entry"
      :note-required="selectedObjectModelAction.require_note"
      :action-title="selectedObjectModelAction.label_title"
    ></action-confirmation-modal>

    <multiple-submissions-modal
      v-if="modalStateFor(vueModalID.multipleSubmissions).isVisible"
      :state="modalStateFor(vueModalID.multipleSubmissions)"
      :index="vueModalID.multipleSubmissions"
      @start-form="startForm"
      @edit-form="editForm"
      @toggle-modal="hideModal(vueModalID.multipleSubmissions)">
    ></multiple-submissions-modal>

    <object-model-action-error-modal
      :state="modalStateFor(vueModalID.objectModelActionError)"
      :index="vueModalID.objectModelActionError"
      @toggle-modal="handleCloseErrorModal"
      :error-title="actionErrorTitle"
      :error-message="actionErrorMessage"
    ></object-model-action-error-modal>

    <object-model-action-custom-notification
      v-if="shouldRenderCustomNotification"
      :state="modalStateFor(vueModalID.objectModelActionCustomNotification)"
      :index="vueModalID.objectModelActionCustomNotification"
      :entry="associatedObjectModelEntry"
      @toggle-modal="handleCloseCustomNotificationModal"
      @send="sendCustomNotification(associatedObjectModelEntry, $event)"
    ></object-model-action-custom-notification>

    <process-flow-submission-app
      v-if="modalStateFor(vueModalID.ProcessFlowSubmissionApp).isVisible"
      :state="modalStateFor(vueModalID.ProcessFlowSubmissionApp)"
      :index="vueModalID.ProcessFlowSubmissionApp"
      :process-flow="processFlow"
      :process-flow-submission-id="processFlowSubmissionId"
      @toggle-modal="processFlowExecutionOnCancel"
      @finished="processFlowExecutionOnFinished"
      @failed="processFlowExecutionOnFailed"
    />
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import ObjectModelFakeEntry from "../../../common/models/objectModelEntries/objectModelFakeEntry";
import ObjectModelEntry from "../../../common/models/objectModelEntries/objectModelEntry";
import ListViewContentElement from "../../../common/models/listViews/elements/listViewContentElement";
import ObjectModelActionErrorModal from "./object-model-action-error-modal.vue";
import ObjectModelActionCustomNotification from "./object-model-action-custom-notification.vue";
import listViewTableMixin from "../mixins/listViewTableMixin";
import selectedMixin from "../mixins/selectedMixin";
import objectModelActionConfirmationMixin from "../mixins/objectModelActionConfirmationMixin";
import objectModelActionPerformMixin from "../mixins/objectModelActionPerformMixin";
import ListView from "~/models/listViews/listView";
import PublishedListView from "~/models/listViews/publishedListView";
import {NotesTabContext} from "../../formSubmission/enums/notesTabContext";
import contentMixin from "../mixins/contextMixin";
import dropdownControlMixin from '~/components/dynamicFieldsTrees/mixins/dropdownControlMixin'
import ListViewMainAction from "~/models/listViews/elements/listViewMainAction";
import {RouteHelper} from "~/helpers/routeHelper";
import {Route} from "~/enums/route";
import { IObjectModelActionViewReference } from '~/interfaces/contentManagementInterfaces';

export default Vue.extend({
  mixins: [listViewTableMixin, objectModelActionConfirmationMixin, selectedMixin, objectModelActionPerformMixin, contentMixin, dropdownControlMixin],
  components: {
    ObjectModelActionErrorModal,
    ObjectModelActionCustomNotification,
    ProcessFlowSubmissionApp: () => import("~/../formability/processFlowSubmission/app_vue.vue"),
  },
  props: {
    contentElement: {
      type: ListViewContentElement,
    },
    isInDetailViewEditMode:{
      type: Boolean,
      default: false
    },
    entry: Object as () => ObjectModelEntry,
    listView: Object as () => ListView, // this is not the same as the selectedPublishedListView for elements in list views embedded in detail views
  },
  data: function (): object {
    return {
      NotesTabContext,
    };
  },
  computed: {
    associatedObjectModelEntry() {
      if (!this.isPreview) return this.entry;
    },
    objectModel() {
      if (this.listView instanceof PublishedListView) {
        return this.listView.published_object_model;
      } else {
        return this.listView.object_model;
      }
    },
    actionReferencesArray(): IObjectModelActionViewReference[] {
      return this.contentElement?.contentElementsArray || this.listView.mainActionsArray;
    },
    orderedActions() {
      return this.hasContentElement
        ? this.contentElementActions
        : this.listViewMainActions;
    },
    contentElementActions() {
      if (!this.listView || !this.associatedObjectModelEntry || this.entry instanceof ObjectModelFakeEntry) return this.contentElement.contentElementsArray;
      return this.contentElement.contentElementsArray.filter(actionElement => {
        const action = this.objectModel.findObjectModelAction(actionElement.action_client_id);
        return this.$can('enable', action, {entry: this.associatedObjectModelEntry, contentView: this.listView})
      });
    },
    listViewMainActions(): ListViewMainAction[] {
      return this.listView.mainActionsArray.filter(listViewMainAction => {
        const action = this.objectModel.findObjectModelAction(listViewMainAction.action_client_id);
        return this.$can('enableMainAction',this.listView, { action });
      })
    },
    hasOnlyOneAction() {
      return this.orderedActions.length === 1;
    },
    objectModelEntryEditFrom(): object {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get("tab") ? {tab: params.get("tab")} : {};

      if (this.selectedPublishedListView) {
        return { from: "List View", from_list_view: this.selectedPublishedListView.list_view_id, ...tab };
      } else if (this.selectedPublishedDetailView) {
        return { from: "Detail View", from_detail_view: this.selectedPublishedDetailView.detail_view_id, from_entry: this.selectedObjectModelEntry.id, ...tab  };
      }
    },
    actionPerformContext(): object {
      // this.listView may be a PublishedListView if the user is viewing a top-level list view, or a ListView if the
      // user is viewing a detail view w/ embedded list view
      const id = this.listView.list_view_id || this.listView.id;
      return {type: "list_view", id};
    },
    hasContentElement(): boolean {
      return !!this.contentElement;
    },
    shouldRenderCustomNotification(): boolean {
      return !!this.entry && !this.isPreview;
    },
    // applies different styles when the action button is rendered for main actions
    singleActionButtonClass(): string {
      return this.hasContentElement
        ? "btn action-btn green"
        : "btn grey mr-1";
    },
    multiActionButtonClass(): string {
      return this.hasContentElement
        ? "dropdown-toggle arrow btn action-btn green"
        : "dropdown-toggle arrow btn grey mr-1";
    },
    newEntryPath(): string {
      const namedWildcardOptions = RouteHelper.namedWildcardOptionsFactory({
        object_model: this.selectedPublishedListView.object_model_id,
      });
      return RouteHelper.path({
        route: Route.objectModelEntriesNew,
        namedWildcardOptions,
        query: {
          from: "Reporting & Submissions Dashboard",
          from_list_view: this.selectedPublishedListView.list_view_id,
        },
      });
    },
  },
  methods: {
    performSelectedAction(): void {
      if (this.entry instanceof ObjectModelFakeEntry) return;
      const objectModelAction = this.selectedObjectModelAction;
      const relevantEntry = this.associatedObjectModelEntry;
      this.performAction(objectModelAction, relevantEntry);
    },
  },
});
</script>